import { buildProgramAbsoluteUrl } from 'bundles/browse-common/utils/url';
import { filterUndefined } from 'bundles/common/utils/commonUtils';
import GrowthDiscoveryEpicClient from 'bundles/epic/clients/GrowthDiscovery';
import {
  LEARNING_PRODUCTS,
  NUM_NO_RESULTS_RECS_PER_PRODUCT_TYPE,
  PRODUCT_TYPES,
} from 'bundles/search-common/constants';
import type { SearchHit, SearchProductCard } from 'bundles/search-common/providers/searchTypes';
import type {
  NoResultsSearchCollection_DiscoveryEntityRatingsReviewsFragment as DiscoveryCollectionRatingReviewEntity,
  NoResultsSearchCollection_DiscoveryCollectionRatingsReviewFragment as DiscoveryCollectionRatingsReview,
  NoResultsSearchCollection_DiscoveryEntityRatingsReviewsFragment as DiscoveryEntity,
} from 'bundles/search-common/queries/__generated__/NoResultsSearchCollectionQuery';
import type { NoSearchResultsPremiumProductCollections_LearningProductFragment as PremiumProduct } from 'bundles/search-common/queries/__generated__/NoSearchResultsPremiumProductCollections';

type CombinedGraphqlResponse = {
  premiumProductCollection?: Array<PremiumProduct | null> | null;
  premiumProductsCollectionLoading?: boolean;
  discoveryCollections?: Array<DiscoveryCollectionRatingsReview | null> | null;
  discoveryCollectionsLoading?: boolean;
};

const getEntityTypeForDiscoveryItem = (entity: DiscoveryEntity) => {
  switch (entity.__typename) {
    case 'DiscoveryCollections_guidedProject':
      return PRODUCT_TYPES.GuidedProject;
    case 'DiscoveryCollections_professionalCertificate':
      return PRODUCT_TYPES.ProfessionalCertificate;
    case 'DiscoveryCollections_project':
      return PRODUCT_TYPES.Project;
    case 'DiscoveryCollections_specialization':
      return PRODUCT_TYPES.Specialization;
    default:
      return PRODUCT_TYPES.Course;
  }
};

const getProductCardForDiscoveryItem = (entity: DiscoveryCollectionRatingReviewEntity): SearchProductCard => {
  return {
    productTypeAttributes: entity.productCard?.productTypeAttributes,
  } as SearchProductCard;
};

const castPremiumProductToSearchHit = (premiumProduct: PremiumProduct): SearchHit => {
  return {
    id: premiumProduct.id,
    name: premiumProduct.name,
    url: buildProgramAbsoluteUrl(premiumProduct.slug, LEARNING_PRODUCTS.Degree),
    productType: PRODUCT_TYPES.Degree,
    imageUrl: premiumProduct.imageUrl || undefined,
    partners: premiumProduct?.partners?.map((partner) => partner?.name).filter(filterUndefined),
    partnerLogos: premiumProduct?.partners?.map((partner) => partner?.logo).filter(filterUndefined),
    __typename: 'Search_ProductHit',
  };
};

const castDiscoveryEntityToSearchHit = (entity: DiscoveryCollectionRatingReviewEntity): SearchHit => {
  const showNoResultsSearch = GrowthDiscoveryEpicClient.preview('showUpdatedNoResultsPage');
  return {
    id: entity.id,
    name: entity.name,
    url: entity.url,
    productType: getEntityTypeForDiscoveryItem(entity),
    imageUrl: entity.imageUrl || undefined,
    partners: entity?.partners?.map((partner) => partner?.name).filter(filterUndefined),
    partnerLogos: entity?.partners?.map((partner) => partner?.logo).filter(filterUndefined),
    productCard: showNoResultsSearch ? getProductCardForDiscoveryItem(entity) : undefined,
    __typename: 'Search_ProductHit',
  };
};

export const combineDegreeAndDiscoveryData = ({
  premiumProductCollection,
  premiumProductsCollectionLoading,
  discoveryCollections,
  discoveryCollectionsLoading,
}: CombinedGraphqlResponse) => {
  const recommendationHits: SearchHit[] = [];

  if (
    !premiumProductsCollectionLoading &&
    premiumProductCollection &&
    !discoveryCollectionsLoading &&
    discoveryCollections
  ) {
    // Convert premium products into search hit items.
    premiumProductCollection?.slice(0, NUM_NO_RESULTS_RECS_PER_PRODUCT_TYPE).forEach((premiumProduct) => {
      if (premiumProduct) {
        recommendationHits.push(castPremiumProductToSearchHit(premiumProduct));
      }
    });

    // Convert discovery collection entities to search hit items.
    discoveryCollections[0]?.entities?.slice(0, NUM_NO_RESULTS_RECS_PER_PRODUCT_TYPE).forEach((entity) => {
      if (entity) {
        recommendationHits.push(castDiscoveryEntityToSearchHit(entity));
      }
    });

    return recommendationHits;
  } else {
    return undefined;
  }
};
export const formatDiscoveryData = (
  discoveryCollections: Array<DiscoveryCollectionRatingsReview | null> | null,
  discoveryCollectionsLoading: boolean
) => {
  const recommendationHits: SearchHit[] = [];

  if (!discoveryCollectionsLoading && discoveryCollections) {
    // Convert discovery collection entities to search hit items.
    discoveryCollections[0]?.entities?.slice(0, 24).forEach((entity) => {
      if (entity) {
        recommendationHits.push(castDiscoveryEntityToSearchHit(entity));
      }
    });

    return recommendationHits;
  } else {
    return undefined;
  }
};
